import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Typography,
  Layout,
  Descriptions,
  Tag,
  Button,
  Progress,
  Space,
  Statistic,
  Row,
  Col,
  Table,
  message,
  Spin,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  UserOutlined,
  CalendarOutlined,
  PercentageOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../Constants";
import AppContext from "../../AppContext";
import { useTranslation } from "react-i18next";

const { Content } = Layout;
const { Title, Text } = Typography;

function CouponDetails() {
  const { t } = useTranslation();
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usageHistoryLoading, setUsageHistoryLoading] = useState(true);
  const [usageHistory, setUsageHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile } = useContext(AppContext);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Lookup/plans`);
      setPlans(response.data || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchCoupon = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/Coupon/${id}`);

      if (response.status === 200) {
        const coupon = response.data;
        setCoupon({
          ...coupon,
          //   expirationDate: dayjs(coupon.expirationDate),
        });
      }
    } catch (error) {
      console.error("Error fetching coupon:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageHistory = async () => {
    setUsageHistoryLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/VoucherUsageHistory?couponId=${id}`
      );

      if (response.status === 200) {
        const usageHistoryData = response.data.data;
        setUsageHistory(usageHistoryData);
      }
    } catch (error) {
      console.error("Error fetching coupon:", error);
    } finally {
      setUsageHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupon();
    fetchUsageHistory();
    fetchPlans();
  }, []);

  if (loading) {
    return (
      <Layout className="min-h-full bg-transparent">
        <div className="flex py-8 items-center max-w-7xl px-4 sm:px-6 lg:px-8">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/coupons")}
            className="mr-4"
          >
            Back
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-white">
            Coupon Details
          </h1>
        </div>
        <Content className="rounded-lg px-6 py-6 shadow bg-white sm:px-8">
          <div>Loading...</div>
        </Content>
      </Layout>
    );
  }

  if (!coupon) {
    return (
      <Layout className="min-h-full bg-transparent">
        <div className="flex py-8 items-center max-w-7xl px-4 sm:px-6 lg:px-8">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/coupons")}
            className="mr-4"
          >
            Back
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-white">
            Coupon Details
          </h1>
        </div>
        <Content className="rounded-lg px-6 py-6 shadow bg-white sm:px-8">
          <div>Coupon not found</div>
        </Content>
      </Layout>
    );
  }

  const getStatusTag = () => {
    if (!coupon.isActive) {
      return <Tag color="default">Inactive</Tag>;
    }
    if (new Date(coupon.expirationDate) < new Date()) {
      return <Tag color="red">Expired</Tag>;
    }
    if (coupon.currentUses >= coupon.maxUses) {
      return <Tag color="orange">Limit Reached</Tag>;
    }
    return <Tag color="green">Active</Tag>;
  };

  const getTypeTag = () => {
    return coupon.type === "coupon" ? (
      <Tag color="blue" icon={<TrophyOutlined />}>
        Trial Coupon
      </Tag>
    ) : (
      <Tag color="purple" icon={<PercentageOutlined />}>
        Discount Code
      </Tag>
    );
  };

  const getDiscountDisplay = () => {
    if (coupon.type === "coupon") {
      return `${coupon.trialDurationDays} days trial`;
    }
    if (coupon.discountType === "percentage") {
      return `${coupon.discountValue}%`;
    }
    return `${coupon.discountValue} SAR`;
  };

  const getProductsDisplay = () => {
    const products = coupon.applicableProducts || [];
    if (products.length === 0) {
      return <Tag>All Products</Tag>;
    }
    return (
      <Space>
        {products.map((product) => (
          <Tag key={product} color="geekblue">
            {product === "digital-card" ? "Digital Card" : product}
          </Tag>
        ))}
      </Space>
    );
  };

  const getPackagesDisplay = () => {
    const products = coupon.applicableProducts || [];
    if (!products.includes("digital-card")) {
      return "N/A";
    }
    const planIds = coupon.applicablePlanIds || [];
    if (planIds.length === 0) {
      return "All Digital Card Plans";
    }
    return planIds
      .map((planId) => plans.find((plan) => plan.id === planId)?.name || planId)
      .join(", ");
  };

  const usagePercentage = usageHistoryLoading ? 0 : (usageHistory.length / coupon.maxUses) * 100;

  const usageColumns = [
    {
      title: "Employee Name",
      dataIndex: "employeeName",
      key: "employeeName",
    },
    {
      title: "Employee Email",
      dataIndex: "employeeEmail",
      key: "employeeEmail",
    },
    {
      title: "Used At",
      dataIndex: "usedAt",
      key: "usedAt",
      render: (date) => new Date(date).toLocaleString(),
    },
    // {
    //   title: "IP Address",
    //   dataIndex: "ipAddress",
    //   key: "ipAddress",
    // },
  ];

  return (
    <Layout className="min-h-full bg-transparent">
      <div className="flex py-8 items-center max-w-7xl px-4 sm:px-6 lg:px-8">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/coupons")}
          className="mr-4"
        >
          Back
        </Button>
        <h1 className="text-2xl font-bold tracking-tight text-white">
          Coupon Details
        </h1>
        <div className="ml-auto">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/coupon-form/${coupon.id}`)}
          >
            Edit Coupon
          </Button>
        </div>
      </div>
      <Content className="rounded-lg px-6 py-6 shadow bg-white sm:px-8">
        <Row gutter={24}>
          <Col span={16}>
            <Card className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Title level={3} className="mb-1">
                    {coupon.code}
                  </Title>
                  <Text type="secondary">{coupon.name}</Text>
                </div>
                <Space>
                  {getTypeTag()}
                  {getStatusTag()}
                </Space>
              </div>

              {coupon.description && (
                <div className="mb-4">
                  <Text>{coupon.description}</Text>
                </div>
              )}

              <Descriptions column={2} bordered>
                <Descriptions.Item label="Coupon Code" span={1}>
                  <Text code copyable>
                    {coupon.code}
                  </Text>
                </Descriptions.Item>
                {/* <Descriptions.Item label="Type" span={1}>
                {getTypeTag()}
              </Descriptions.Item> */}
                <Descriptions.Item label="Value" span={1}>
                  {getDiscountDisplay()}
                </Descriptions.Item>
                <Descriptions.Item label="Max Uses Per User" span={1}>
                  {coupon.maxUsesPerUser != null
                    ? coupon.maxUsesPerUser
                    : "Unlimited"}
                </Descriptions.Item>
                <Descriptions.Item label="Product" span={1}>
                  {getProductsDisplay()}
                </Descriptions.Item>
                <Descriptions.Item label="Packages" span={1}>
                  {getPackagesDisplay()}
                </Descriptions.Item>
                {/* <Descriptions.Item label="Status" span={1}>
                {getStatusTag()}
              </Descriptions.Item> */}
                <Descriptions.Item label="Created Date" span={1}>
                  {new Date(coupon.createdAt).toLocaleDateString()}
                </Descriptions.Item>
                <Descriptions.Item label="Expiration Date" span={1}>
                  <Space>
                    <CalendarOutlined />
                    {new Date(coupon.expirationDate).toLocaleDateString()}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Created By" span={2}>
                  <Space>
                    <UserOutlined />
                    {coupon.createdBy}
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Usage History" className="mb-6">
              <Table
                columns={usageColumns}
                dataSource={usageHistory}
                rowKey="id"
                loading={usageHistoryLoading}
                pagination={{
                  pageSize: 5,
                  showSizeChanger: false,
                }}
                size="small"
              />
            </Card>
          </Col>

          <Col span={8}>
            <Card className="mb-6">
              <Spin spinning={usageHistoryLoading}>
                <Statistic
                  title="Total Uses"
                  value={usageHistoryLoading ? 0 : usageHistory.length}
                  suffix={`/ ${coupon.maxUses}`}
                  prefix={<UserOutlined />}
                />
                <Progress
                  percent={usageHistoryLoading ? 0 : usagePercentage}
                  status={
                    usageHistoryLoading 
                      ? "normal"
                      : usagePercentage >= 100
                      ? "exception"
                      : usagePercentage >= 80
                      ? "active"
                      : "normal"
                  }
                  className="mt-2"
                />
              </Spin>
            </Card>

            <Card className="mb-6">
              <Spin spinning={usageHistoryLoading}>
                <Statistic
                  title="Remaining Uses"
                  value={usageHistoryLoading ? coupon.maxUses : Math.max(0, coupon.maxUses - usageHistory.length)}
                  valueStyle={{
                    color:
                      usageHistory.length >= coupon.maxUses
                        ? "#ff4d4f"
                        : "#3f8600",
                  }}
                />
              </Spin>
            </Card>

            <Card>
              <Statistic
                title="Days Until Expiration"
                value={Math.max(
                  0,
                  Math.ceil(
                    (new Date(coupon.expirationDate) - new Date()) /
                      (1000 * 60 * 60 * 24)
                  )
                )}
                suffix="days"
                valueStyle={{
                  color:
                    new Date(coupon.expirationDate) <= new Date()
                      ? "#ff4d4f"
                      : Math.ceil(
                          (new Date(coupon.expirationDate) - new Date()) /
                            (1000 * 60 * 60 * 24)
                        ) <= 7
                      ? "#fa8c16"
                      : "#3f8600",
                }}
              />
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}

export default CouponDetails;
